import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AlertsTableConfig } from "../../../../../../@types/Alerts";
import { Notification } from "../../../../../../@types/Notification";
import { Button } from "../../../../../../components/Button";
import { Checkbox } from "../../../../../../components/Checkbox";
import { userService } from "../../../../../../services/userService";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import {
  CancelButton,
  ColumnTitle,
  Container,
  Row,
  SubmitContainer,
  Subtitle,
  Title,
} from "./AlertsTableConfigModal.styles";

interface IAlertsTableConfigModalProps {
  columnsConfig: AlertsTableConfig;
  onClose(
    listColumnConfig?: AlertsTableConfig,
    notification?: Notification
  ): void;
}

export function AlertsTableConfigModal({
  columnsConfig,
  onClose,
}: IAlertsTableConfigModalProps) {
  const { t } = useTranslation();

  const [confirmChanges, setConfirmChanges] = useState(false);
  const [listColumnConfig, setListColumnConfig] =
    useState<AlertsTableConfig>(columnsConfig);

  useEffect(() => {
    mixpanelActions.track("Visited Screen: AlertsTabbleConfiguration");
  }, []);

  const handleValueChange = (value: boolean, propertyName: string) => {
    mixpanelActions.track(`User Action: Checkbox ${propertyName}`);

    const temporaryList = { ...listColumnConfig } as any;
    temporaryList[propertyName] = value;

    setListColumnConfig(temporaryList);
  };

  const handleOnConfirm = async () => {
    mixpanelActions.track("User Action: AlertsTabbleConfigurationConfirm");
    if (confirmChanges) {
      const tableConfigJson = JSON.stringify(listColumnConfig);

      const result = await userService.updateAlertsTableColumnPreferences(
        tableConfigJson
      );

      if (result.status >= 200 && result.status < 300) {
        onClose(listColumnConfig, {
          show: true,
          message: t("patientPage_alertsConfig_saved"),
          type: "success",
          width: "max-content",
        });
        return;
      }

      onClose(undefined, {
        show: true,
        message: t("common_genericError"),
        type: "error",
        width: "max-content",
      });
    }

    setConfirmChanges(true);
  };

  return (
    <Container>
      {!confirmChanges ? (
        <>
          <Row style={{ marginBottom: "26px" }}>
            <ColumnTitle>{t("patientPage_alertsConfig_column")}</ColumnTitle>
            <ColumnTitle>{t("patientPage_alertsConfig_show")}</ColumnTitle>
          </Row>

          <Row disabled>
            <p>{t("patientPage_alerts_columns_type")}</p>
            <Checkbox
              checked={listColumnConfig.type}
              onChange={() => {}}
              label=""
              labelPosition="before"
              disabled
            />
          </Row>

          <Row disabled>
            <p>{t("patientPage_alerts_columns_trigger")}</p>
            <Checkbox
              checked={listColumnConfig.trigger}
              onChange={() => {}}
              label=""
              labelPosition="before"
              disabled
            />
          </Row>

          <Row>
            <p>{t("patientPage_alerts_columns_value")}</p>
            <Checkbox
              checked={listColumnConfig.value}
              onChange={(isChecked) => handleValueChange(isChecked, "value")}
              label=""
              labelPosition="before"
            />
          </Row>

          <Row>
            <p>{t("patientPage_alerts_columns_summary")}</p>
            <Checkbox
              checked={listColumnConfig.summary}
              onChange={(isChecked) => handleValueChange(isChecked, "summary")}
              label=""
              labelPosition="before"
            />
          </Row>

          <Row>
            <p>{t("patientPage_alerts_columns_timeFrame")}</p>
            <Checkbox
              checked={listColumnConfig.timeFrame}
              onChange={(isChecked) =>
                handleValueChange(isChecked, "timeFrame")
              }
              label=""
              labelPosition="before"
            />
          </Row>

          <Row>
            <p>{t("patientPage_alerts_columns_status")}</p>
            <Checkbox
              checked={listColumnConfig.alertStatus}
              onChange={(isChecked) =>
                handleValueChange(isChecked, "alertStatus")
              }
              label=""
              labelPosition="before"
            />
          </Row>

          <Row>
            <p>{t("patientPage_alerts_columns_timeOfAlert")}</p>
            <Checkbox
              checked={listColumnConfig.timeOfAlert}
              onChange={(isChecked) =>
                handleValueChange(isChecked, "timeOfAlert")
              }
              label=""
              labelPosition="before"
            />
          </Row>

          <Row>
            <p>{t("patientPage_alerts_columns_resolvedBy")}</p>
            <Checkbox
              checked={listColumnConfig.resolvedBy}
              onChange={(isChecked) =>
                handleValueChange(isChecked, "resolvedBy")
              }
              label=""
              labelPosition="before"
            />
          </Row>
        </>
      ) : (
        <>
          <Title>{t("patientPage_alertsConfig_confirmTitle")}</Title>
          <Subtitle larger style={{ marginBottom: "-30px", fontWeight: "400" }}>
            {t("patientPage_alertsConfig_confirmBody")}
          </Subtitle>
        </>
      )}

      <SubmitContainer style={{ margin: "49px 0 8px 3px" }}>
        <Button
          label={t("patientPage_alertsConfig_confirm")}
          type="button"
          onClick={handleOnConfirm}
          isLoading={false}
        />

        <CancelButton
          onClick={() => {
            mixpanelActions.track("User Action: ConfigurePatientListCancel");
            onClose(columnsConfig);
          }}
          style={{ marginLeft: "-34px" }}
        >
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </Container>
  );
}
